import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import {
    backgroundColor,
    backgroundColorActive,
    borderColor,
    disabledText,
    link,
    primaryText,
} from 'styles/theme';

// Common styles that are reused
const commonFontStyles = css`
    font-size: 1.4rem;
    font-style: normal;
    line-height: 150%;
    letter-spacing: 0.025rem;
`;

const commonCellAlignment = css`
    &:first-child {
        text-align: left;
    }
`;

const commonFlexAlignment = css`
    display: flex;
    align-items: center;
    gap: 0.8rem;
    min-width: 0;
    width: 100%;
`;

// Container components
export const TableContainer = styled.div`
    position: relative;
    display: flex;
    flex: 1;
    flex-direction: column;
    overflow: auto;
    min-height: 0;
`;

export const Table = styled.table`
    width: calc(100% - 0.8rem); /* Subtract the container's padding */
    border-collapse: collapse;
    table-layout: fixed;
`;

// Header components
export const TableHead = styled.thead`
    position: sticky;
    top: 0;
    z-index: 1;
    background: ${backgroundColor};
`;

export const TableHeader = styled.th<{
    $sortable?: boolean;
    $isSorted?: boolean;
    $sortDirection?: 'asc' | 'desc';
}>`
    ${commonFontStyles}
    ${commonCellAlignment}
    cursor: ${({ $sortable }) => ($sortable ? 'pointer' : 'default')};
    user-select: none;
    padding: 0.8rem;
    text-align: right;
    font-weight: 500;
    color: ${primaryText};
    border-bottom: 0.1rem solid ${borderColor};
    height: 5.2rem;
    white-space: pre-wrap;
    overflow-wrap: normal;

    &:hover {
        background-color: ${({ $sortable }) =>
            $sortable ? backgroundColorActive : backgroundColor};
    }
`;

export const ContentWrapper = styled.div`
    ${commonFlexAlignment}
    justify-content: flex-end;
    text-align: inherit;

    ${TableHeader}:first-child & {
        justify-content: flex-start;
    }
`;

// Body components
export const TableBody = styled.tbody``;

export const TableHeaderRow = styled.tr``;

export const TableRow = styled.tr<{
    $isExpandable?: boolean;
    $isExpanded?: boolean;
    $nestingLevel?: number;
}>`
    ${commonFontStyles}
    font-weight: 500;
    background: ${backgroundColor};
    transition: background-color 0.2s ease;

    ${({ $isExpandable }) =>
        $isExpandable &&
        css`
            cursor: pointer;
        `};

    tbody & {
        &:hover {
            background: ${backgroundColorActive};
        }
    }
`;

export const TableCell = styled.td`
    ${commonFontStyles}
    ${commonCellAlignment}
    font-weight: 300;
    padding: 0.8rem;
    text-align: right;
    border-bottom: 0.1rem solid ${borderColor};
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;

    &:first-child {
        white-space: pre-wrap;
        overflow-wrap: normal;
    }
`;

// Special state row that centers content and removes borders
export const SpecialStateRow = styled(TableRow)`
    height: 20rem;
`;

export const SpecialStateCell = styled.td`
    ${commonFontStyles}
    ${commonCellAlignment}
    font-weight: 300;
    padding: 0.8rem;
    text-align: right;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
`;

// Link components
export const TableExternalLinkIcon = styled(FontAwesomeIcon)`
    font-size: 1.2rem;
    color: ${link};
    opacity: 0;
    transition: opacity 0.2s ease;
    flex-shrink: 0;
`;

export const TableLink = styled(Link)<{ $isSystem?: boolean }>`
    color: ${link};
    text-decoration: none;
    display: flex;
    align-items: center;
    max-width: 100%;
    min-width: 0;
    gap: 0.4rem;
    padding-left: ${(props) => (props.$isSystem ? '3rem' : '0')};

    &:hover {
        text-decoration: underline;

        ${TableExternalLinkIcon} {
            opacity: 1;
        }
    }
`;

// Icon components
export const StyledSortIcon = styled(FontAwesomeIcon)<{ $isSorted?: boolean }>`
    flex-shrink: 0;
    font-size: 1.2rem;
    margin-top: 0;
    color: ${({ $isSorted }) => ($isSorted ? primaryText : disabledText)};

    th:hover & {
        color: ${primaryText};
    }
`;

export const ExpandIcon = styled(FontAwesomeIcon).attrs<{
    $isExpanded?: boolean;
}>({})`
    flex-shrink: 0;
    opacity: 0.66;
    font-size: 1.2rem;
    width: 1.2rem;
    margin-right: 0.8rem;
    transition: transform 0.2s ease;
    transform: ${({ $isExpanded }) => ($isExpanded ? 'rotate(90deg)' : 'rotate(0deg)')};
`;

export const CellContentWrapper = styled.div`
    ${commonFlexAlignment}
    ${commonFontStyles}
    justify-content: flex-end;
    text-align: inherit;
    font-weight: inherit;
    color: inherit;

    ${TableCell}:first-child & {
        justify-content: flex-start;
    }
`;
